<div class="flex items-center justify-between mb-4">
  <h2 class="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-[0.15em]">Recent Ingestion Jobs</h2>
  <div class="flex items-center gap-2">
    <div class="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
    <span class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Live auto-refresh</span>
  </div>
</div>

@if (listError) {
  <div class="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-3">
    {{ listError }}
  </div>
}

<div class="bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800 rounded-2xl shadow-sm overflow-hidden transition-colors duration-300">
  <div class="overflow-x-auto">
    <table class="w-full text-sm">
      <thead class="bg-gray-50 dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 text-[10px] uppercase font-bold tracking-widest text-gray-400 dark:text-gray-500">
        <tr>
          <th class="text-left font-medium px-4 py-3">File</th>
          <th class="text-left font-medium px-4 py-3">Type</th>
          <th class="text-left font-medium px-4 py-3">Uploader</th>
          <th class="text-left font-medium px-4 py-3">Status</th>
          <th class="text-left font-medium px-4 py-3">Size</th>
          <th class="text-left font-medium px-4 py-3">Created</th>
          <th class="text-left font-medium px-4 py-3">Finished</th>
          <th class="text-left font-medium px-4 py-3 w-12">Details</th>
        </tr>
      </thead>
      <tbody>
        @if (isLoading && jobs.length === 0) {
          <tr class="bg-transparent">
            <td colspan="8" class="px-4 py-8 text-center text-gray-400 dark:text-gray-500">Loading jobs…</td>
          </tr>
        } @else if (!isLoading && jobs.length === 0) {
          <tr class="bg-transparent">
            <td colspan="8" class="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
              No jobs yet. Use the File Browser to upload and parse or ingest files.
            </td>
          </tr>
        } @else {
          @for (job of jobs; track trackByJob($index, job)) {
            <tr class="bg-transparent border-t border-gray-100 dark:border-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
              <td class="px-6 py-4">
                <div class="font-bold text-gray-900 dark:text-white truncate max-w-xs transition-colors" [title]="job.originalFilename">
                  {{ job.originalFilename }}
                </div>
                <div class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mt-1 truncate max-w-xs" [title]="job.s3Key">
                  {{ job.s3Key }}
                </div>
              </td>
              <td class="px-6 py-4">
                <span
                  class="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  [ngClass]="jobTypeClass(job.jobType)">
                  {{ job.jobType }}
                </span>
              </td>
              <td class="px-6 py-4">
                @if (job.uploader) {
                  <div class="flex items-center gap-3">
                    <div class="w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-xs font-bold text-blue-500">
                      {{ initials(job.uploader.username) }}
                    </div>
                    <div class="min-w-0">
                      <div class="text-xs font-bold text-gray-900 dark:text-white truncate">{{ job.uploader.username }}</div>
                      <div class="text-[10px] font-semibold text-gray-400 dark:text-gray-500 truncate">{{ job.uploader.email }}</div>
                    </div>
                  </div>
                } @else {
                  <span class="text-gray-400 dark:text-gray-500">—</span>
                }
              </td>
              <td class="px-6 py-4">
                <span
                  class="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
                  [ngClass]="statusClass(job.status)">
                  {{ job.status }}
                </span>
              </td>
              <td class="px-6 py-4 text-xs font-semibold text-gray-600 dark:text-gray-300">{{ formatBytes(job.fileSizeBytes) }}</td>
              <td class="px-6 py-4 text-xs font-semibold text-gray-600 dark:text-gray-300">{{ formatDate(job.createdAt) }}</td>
              <td class="px-6 py-4 text-xs font-semibold text-gray-600 dark:text-gray-300">{{ formatDate(job.finishedAt) }}</td>
              <td class="px-6 py-4">
                @if (job.statusMessage) {
                  <div class="flex justify-center">
                    <button
                      cdkOverlayOrigin
                      #tooltipOrigin="cdkOverlayOrigin"
                      type="button"
                      class="flex items-center justify-center w-7 h-7 rounded-full transition-colors"
                      [ngClass]="detailsIconClass(job.status)"
                      (mouseenter)="showTooltip(job.id)"
                      (mouseleave)="hideTooltip()"
                      aria-label="View details">
                      <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </button>
                    <ng-template
                      cdkConnectedOverlay
                      [cdkConnectedOverlayOrigin]="tooltipOrigin"
                      [cdkConnectedOverlayOpen]="activeTooltipJobId === job.id"
                      [cdkConnectedOverlayPositions]="tooltipPositions"
                      [cdkConnectedOverlayHasBackdrop]="false">
                      <div class="w-72 bg-gray-900 border border-gray-700 rounded-lg shadow-xl px-3 py-2.5 pointer-events-none">
                        <p class="text-[11px] leading-relaxed text-gray-200 break-words whitespace-pre-wrap">{{ job.statusMessage }}</p>
                      </div>
                    </ng-template>
                  </div>
                } @else {
                  <span class="block text-center text-gray-300 dark:text-gray-600">—</span>
                }
              </td>
            </tr>
          }
        }
      </tbody>
    </table>
  </div>
</div>

@if (totalPages > 1) {
  <div class="flex items-center justify-between mt-4 px-1">
    <span class="text-xs text-gray-400 dark:text-gray-500">
      Page {{ currentPage }} of {{ totalPages }} &mdash; {{ totalCount }} total
    </span>
    <div class="flex items-center gap-1">
      <button
        type="button"
        [disabled]="currentPage === 1"
        (click)="pageChange.emit(currentPage - 1)"
        class="flex items-center justify-center w-8 h-8 rounded-lg text-sm font-medium transition-colors
               disabled:opacity-30 disabled:cursor-not-allowed
               text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 enabled:cursor-pointer">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      @for (page of pageNumbers; track page) {
        <button
          type="button"
          (click)="pageChange.emit(page)"
          [ngClass]="page === currentPage
            ? 'bg-blue-600 text-white'
            : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'"
          class="flex items-center justify-center w-8 h-8 rounded-lg text-xs font-semibold transition-colors cursor-pointer">
          {{ page }}
        </button>
      }
      <button
        type="button"
        [disabled]="currentPage === totalPages"
        (click)="pageChange.emit(currentPage + 1)"
        class="flex items-center justify-center w-8 h-8 rounded-lg text-sm font-medium transition-colors
               disabled:opacity-30 disabled:cursor-not-allowed
               text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 enabled:cursor-pointer">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>
}
